import type {
  ReplaceNumberStatusResponse,
  ReplacementFailureCode,
} from "@/services/whatsappNumberReplacementService";

/**
 * The single place where an Atlas `ReplacementStatus` becomes something a
 * human reads. Every other component derives its behaviour from
 * {@link describeReplacement} rather than switching on the raw status, so
 * there is exactly one definition of "is this done", "is the channel locked"
 * and "should we keep polling".
 *
 * Three rules this file exists to enforce:
 *
 * 1. **`done` is true for `COMPLETED` and nothing else.** Not for a 200
 *    response, not for a reached stage, not for an unrecognised status.
 * 2. **The channel-lock mirror is `ReplacementStatus.releasesChannelLock()`**
 *    (COMPLETED / ROLLED_BACK / VERIFICATION_FAILED release it; everything
 *    else — including CHATWOOT_DISPLAY_PENDING, CUTOVER_FAILED and
 *    ROLLBACK_FAILED — holds it). This is a *mirror* of the backend's
 *    DB-enforced lock, never a substitute for it.
 * 3. **An unknown status fails safe**: not done, channel locked, keep polling.
 *    A backend that adds a state must never make this UI claim success.
 */

export type ReplacementTone = "progress" | "success" | "warning" | "danger";

export interface ReplacementPresentation {
  /** Raw backend value, echoed so diagnostics can show exactly what arrived. */
  status: string;
  /** Short headline, e.g. "Switching WhatsApp traffic". */
  label: string;
  /** One or two sentences of plain-English explanation. Never overstates. */
  description: string;
  tone: ReplacementTone;
  /**
   * True ONLY for `COMPLETED`. The literal answer to "may the UI say the
   * replacement finished successfully?".
   */
  done: boolean;
  /**
   * True while the backend holds the per-channel replacement lock, i.e.
   * another replacement cannot be started. Mirrors
   * `!ReplacementStatus.releasesChannelLock()`.
   */
  locksChannel: boolean;
  /** True while the backend is still expected to move on its own. */
  inProgress: boolean;
  /** True when the state needs a human before it can move. */
  needsOperator: boolean;
  /** True when this UI should keep polling for updates. */
  shouldPoll: boolean;
  /** True when the UI recognised the status at all. */
  known: boolean;
  /** Rough 0–100 progress for the bar. Never 100 unless {@link done}. */
  progress: number;
}

type Spec = Omit<ReplacementPresentation, "status" | "known">;

const IN_PROGRESS = { inProgress: true, needsOperator: false, shouldPoll: true, done: false } as const;
const SETTLED = { inProgress: false, shouldPoll: false } as const;

const SPECS: Record<string, Spec> = {
  REQUESTED: {
    ...IN_PROGRESS,
    label: "Replacement requested",
    description:
      "BridgeAI has recorded the request. Nothing has changed yet — the current number is still live.",
    tone: "progress",
    locksChannel: true,
    progress: 5,
  },
  VERIFYING: {
    ...IN_PROGRESS,
    label: "Verifying the new WhatsApp number",
    description:
      "Checking with Meta that the new number, its phone number ID and the access token all match. The current number is still live.",
    tone: "progress",
    locksChannel: true,
    progress: 15,
  },
  VERIFIED: {
    ...IN_PROGRESS,
    label: "New number verified",
    description:
      "Meta confirmed the new number. The switchover is about to begin — messaging is still on the current number.",
    tone: "progress",
    locksChannel: true,
    progress: 30,
  },
  CUTOVER_IN_PROGRESS: {
    ...IN_PROGRESS,
    label: "Switching WhatsApp traffic",
    description:
      "Routing for this channel is being moved to the new number. Do not close this page's channel configuration until it settles.",
    tone: "progress",
    locksChannel: true,
    progress: 50,
  },
  CHAT_GATEWAY_PENDING: {
    ...IN_PROGRESS,
    label: "New number is live — finishing routing updates",
    description:
      "Customer messaging has moved to the new number. BridgeAI is finishing the remaining routing updates.",
    tone: "progress",
    locksChannel: true,
    progress: 70,
  },
  VERIFYING_NEW_PATH: {
    ...IN_PROGRESS,
    label: "Confirming the new number is live",
    description:
      "Messaging is on the new number. BridgeAI is reading the routing back to confirm it before finishing.",
    tone: "progress",
    locksChannel: true,
    progress: 85,
  },
  CHATWOOT_DISPLAY_PENDING: {
    ...SETTLED,
    label: "WhatsApp number changed — Chatwoot update pending",
    description:
      "Customer messaging has moved to the new number and is working. Only Chatwoot's displayed number has not been updated yet. " +
      "This channel stays locked until that finishes, so another number change cannot be started.",
    tone: "warning",
    done: false,
    locksChannel: true,
    needsOperator: true,
    progress: 90,
  },
  COMPLETED: {
    ...SETTLED,
    label: "WhatsApp number replaced",
    description:
      "The channel now uses the new number. The inbox, conversations, agents and bot configuration were preserved.",
    tone: "success",
    done: true,
    locksChannel: false,
    needsOperator: false,
    progress: 100,
  },
  VERIFICATION_FAILED: {
    ...SETTLED,
    label: "Replacement rejected — nothing was changed",
    description:
      "The new number did not pass verification, so no routing was touched. The current number is unchanged and you can correct the details and try again.",
    tone: "danger",
    done: false,
    locksChannel: false,
    needsOperator: false,
    progress: 0,
  },
  CUTOVER_FAILED: {
    ...SETTLED,
    label: "Switchover failed — needs attention",
    description:
      "A step of the switchover failed after routing had started to move. BridgeAI has not finished and has not rolled back on its own. " +
      "Contact support before starting another change on this channel.",
    tone: "danger",
    done: false,
    locksChannel: true,
    needsOperator: true,
    progress: 50,
  },
  ROLLBACK_IN_PROGRESS: {
    ...IN_PROGRESS,
    label: "Rolling back to the previous number",
    description:
      "The switchover could not complete, so BridgeAI is restoring the previous number. This channel stays locked until it finishes.",
    tone: "warning",
    locksChannel: true,
    progress: 40,
  },
  ROLLED_BACK: {
    ...SETTLED,
    label: "Rolled back — previous number restored",
    description:
      "The replacement did not complete and the previous number has been restored and confirmed live. You can correct the details and try again.",
    tone: "danger",
    done: false,
    locksChannel: false,
    needsOperator: false,
    progress: 0,
  },
  ROLLBACK_FAILED: {
    ...SETTLED,
    label: "Rollback failed — manual attention required",
    description:
      "The replacement failed and the automatic restore of the previous number did not finish. " +
      "Some of the restore steps may have completed and some may not. This channel stays locked until " +
      "the remaining steps succeed — once the underlying problem is fixed, you can retry the rollback.",
    tone: "danger",
    done: false,
    locksChannel: true,
    needsOperator: true,
    progress: 40,
  },
};

/**
 * Fail-safe presentation for a status this build has never heard of. Chosen so
 * that a backend adding a state can only ever make the UI more cautious:
 * not done, channel locked, still polling.
 */
function unknownSpec(status: string): Spec {
  return {
    label: "Replacement in an unrecognised state",
    description:
      `BridgeAI reported the state "${status}", which this version of the console does not recognise. ` +
      "The replacement is NOT confirmed as finished. This channel stays locked — contact support if it does not resolve.",
    tone: "warning",
    done: false,
    locksChannel: true,
    inProgress: false,
    needsOperator: true,
    shouldPoll: true,
    progress: 50,
  };
}

/** Presentation for one backend status value. */
export function describeStatus(rawStatus: string | null | undefined): ReplacementPresentation {
  const status = (rawStatus ?? "").trim();
  if (!status) {
    return {
      ...unknownSpec("(missing)"),
      status: "",
      known: false,
      description:
        "BridgeAI did not report a replacement state. The replacement is NOT confirmed as finished — " +
        "this channel stays locked until a state is known.",
    };
  }
  const spec = SPECS[status];
  return spec
    ? { ...spec, status, known: true }
    : { ...unknownSpec(status), status, known: false };
}

/** Presentation for a whole status response. `null` means "no replacement ever". */
export function describeReplacement(
  op: ReplaceNumberStatusResponse | null | undefined,
): ReplacementPresentation | null {
  if (!op) return null;
  return describeStatus(typeof op.status === "string" ? op.status : null);
}

/**
 * True when a replacement record should still be shown on the channel.
 *
 * A COMPLETED / ROLLED_BACK / VERIFICATION_FAILED record from days ago is
 * history, not current state — but one that just resolved in this session is
 * worth showing, so the caller decides via `sinceSessionStart`.
 */
export function holdsChannelLock(op: ReplaceNumberStatusResponse | null | undefined): boolean {
  const presentation = describeReplacement(op);
  return presentation?.locksChannel ?? false;
}

/**
 * User-facing copy for every `ReplacementFailureCode`. Raw backend
 * `failureMessage` values are hand-authored and safe to show, but these are
 * clearer and stable, so they win where a code is present.
 */
const FAILURE_MESSAGES: Record<ReplacementFailureCode, string> = {
  CHANNEL_NOT_FOUND: "This communication channel no longer exists. Refresh the page and try again.",
  UNAUTHORIZED_FOR_CHANNEL: "You do not have access to change the WhatsApp number on this channel.",
  INVALID_REQUEST: "Some of the details supplied were not valid. Check the number, phone number ID and token.",
  REPLACEMENT_ALREADY_IN_PROGRESS:
    "A number change is already in progress for this channel. Wait for it to finish before starting another.",
  CHANNEL_MISSING_CURRENT_WHATSAPP_CONFIG:
    "This channel has no WhatsApp phone number ID or business account ID yet. Finish WhatsApp onboarding before replacing the number.",
  TOKEN_INVALID_FOR_WABA:
    "Meta could not validate the access token, or it does not grant access to this WhatsApp Business Account.",
  PHONE_NUMBER_WABA_MISMATCH:
    "That phone number ID does not belong to this WhatsApp Business Account. Check the ID in Meta Business Manager.",
  DISPLAY_NUMBER_MISMATCH:
    "The number displayed by Meta for that phone number ID is different from the number you entered.",
  DUPLICATE_PHONE_NUMBER: "That WhatsApp number is already in use by another BridgeAI channel.",
  SAME_NUMBER_AS_CURRENT: "That is the number this channel already uses — there is nothing to change.",
  CROSS_WABA_REPLACEMENT_NOT_ENABLED:
    "The new number belongs to a different WhatsApp Business Account. Only replacing a number within the same business account is supported.",
  WA_GATEWAY_BRANCH_NOT_FOUND:
    "BridgeAI could not find the routing record for this channel. Contact support — no number change was applied.",
  WA_GATEWAY_CUTOVER_FAILED: "Routing could not be switched to the new number.",
  CHAT_GATEWAY_PREPARE_FAILED: "The new number's routing could not be prepared. No traffic was moved.",
  CHAT_GATEWAY_FINALIZE_FAILED: "The previous number's routing could not be retired.",
  NEW_PATH_VERIFICATION_FAILED: "BridgeAI could not confirm the new number was live after switching.",
  PROMOTION_FAILED: "The channel record could not be updated with the new number.",
  ROLLBACK_WA_GATEWAY_FAILED:
    "Restoring routing to the previous number failed. Once the underlying problem is fixed you can retry " +
    "the rollback; if it keeps failing, this channel needs BridgeAI support.",
  ROLLBACK_CHAT_GATEWAY_FAILED:
    "Traffic was restored to the previous number but the routing mapping was not. Once the underlying " +
    "problem is fixed you can retry the rollback; if it keeps failing, this channel needs BridgeAI support.",
  NO_CHATWOOT_DISPLAY_RETRY_PENDING:
    "There is no pending Chatwoot update on this channel to retry. Refresh to see the current state.",
  NO_ROLLBACK_RETRY_PENDING:
    "There is no failed rollback on this channel to retry. Refresh to see the current state.",
  INTERNAL_ERROR: "An unexpected error occurred. Contact support with the replacement ID shown below.",
};

/**
 * Best available user-facing explanation for a failed/rejected replacement.
 * Prefers the mapped code; falls back to the backend's hand-authored message
 * (never a raw exception — the backend maps those away) and then to a generic.
 */
export function describeFailure(
  failureCode: string | null | undefined,
  failureMessage?: string | null,
): string | null {
  const code = (failureCode ?? "").trim();
  if (code && code in FAILURE_MESSAGES) {
    return FAILURE_MESSAGES[code as ReplacementFailureCode];
  }
  const message = failureMessage?.trim();
  if (message) return message;
  if (code) return `The replacement failed (${code}). Contact support if this persists.`;
  return null;
}

/** Codes that mean "nothing live was touched, fix the input and retry". */
export const CORRECTABLE_FAILURE_CODES: ReadonlySet<string> = new Set([
  "INVALID_REQUEST",
  "TOKEN_INVALID_FOR_WABA",
  "PHONE_NUMBER_WABA_MISMATCH",
  "DISPLAY_NUMBER_MISMATCH",
  "DUPLICATE_PHONE_NUMBER",
  "SAME_NUMBER_AS_CURRENT",
  "CROSS_WABA_REPLACEMENT_NOT_ENABLED",
  "CHANNEL_MISSING_CURRENT_WHATSAPP_CONFIG",
]);
